import { useGetAccountWithinCareseekerGroup } from "apollo/hooks/mutations";
import {
  WHITELISTED_DOMAIN_REGEX,
  formatDomainError,
  getEmailFromAccountCreationError,
  matchInputAgainstRegex,
} from "core/model/accounts/accountErrorHandling";
import { Account, Careseeker } from "core/types";
import { Dispatch, SetStateAction, useCallback } from "react";
import { validateEmail } from "react-forms-state";
import { useTranslations } from "translations";
import { AccountDialogState, STEP_1, STEP_2 } from "./useUserManagementFeature";

export function useAccountByEmailHandler({
  careseeker,
  setAccountDialog,
}: {
  careseeker: Careseeker | undefined;
  setAccountDialog: Dispatch<SetStateAction<AccountDialogState>>;
}) {
  const translations = useTranslations();

  const [getAccountByEmail, getAccountProgress] =
    useGetAccountWithinCareseekerGroup({
      careseekerId: careseeker?.id || -1,
    });

  const handleGetAccount = useCallback(
    async ({ email }: { email: string | null }) => {
      const isValid = validateEmail({ email }) === true;

      if (!email || !isValid) {
        setAccountDialog((prev) => ({
          ...prev,
          error: !email
            ? translations.userManagement.addAccountFlow.mandatoryFieldEmail
            : translations.people.invalidEmailAddress,
        }));

        return;
      }
      try {
        const { data } = await getAccountByEmail({ email });
        if (data?.account) {
          // Account already exists for another careseeker
          setAccountDialog((prev) => ({
            ...prev,
            account: data.account,
            error: undefined,
            isOtherCareseekerAccount: true,
            prevStep: STEP_1,
            step: STEP_2,
          }));
          return;
        }
      } catch (err) {
        const { result, statusCode } = (err as any).networkError;
        const backendError = result?.error;

        if (
          statusCode === 403 &&
          (result.error as string).includes("associated with another company")
        ) {
          // Account already exists outside of the company group
          setAccountDialog((prev) => ({
            ...prev,
            error: translations.userManagement.errorAccountOutsideGroup,
          }));
          return;
        }

        if (statusCode === 409) {
          // Account already exists for this careseeker
          setAccountDialog((prev) => ({
            ...prev,
            error: translations.userManagement.errorEmailAlreadyExists({
              emailAddress: email,
            }),
          }));
          return;
        }

        if (!backendError) {
          // No account exists
          setAccountDialog((prev) => ({
            ...prev,
            account: { email } as Account,
            isOtherCareseekerAccount: false,
            prevStep: STEP_1,
            step: STEP_2,
          }));
          return;
        }

        const whiteListedDomainError = matchInputAgainstRegex(
          backendError,
          WHITELISTED_DOMAIN_REGEX,
        );
        if (whiteListedDomainError) {
          setAccountDialog((prev) => ({
            ...prev,
            error: translations.userManagement.errorDomainNotWhitelisted,
          }));
          return;
        }

        const domainError = formatDomainError(backendError, translations);
        if (domainError) {
          setAccountDialog((prev) => ({ ...prev, error: domainError }));
          return;
        }

        const emailAddress = getEmailFromAccountCreationError({
          message: backendError,
        });
        if (emailAddress) {
          setAccountDialog((prev) => ({
            ...prev,
            error: translations.userManagement.errorEmailAlreadyExists({
              emailAddress,
            }),
          }));
          return;
        }
      }
    },
    [
      formatDomainError,
      getAccountByEmail,
      getEmailFromAccountCreationError,
      matchInputAgainstRegex,
      setAccountDialog,
      validateEmail,
    ],
  );

  return { getAccountProgress, handleGetAccount };
}
